<script lang="ts">
 /**
 * Full-screen drawer for reviewing captured photos before analysis.
 *
 * @remarks
 * Shows all photo thumbnails with individual remove buttons, an add-photo
 * button, and an optional text/voice hint field. The hint uses progressive
 * disclosure — hidden behind an "Add a hint..." link until tapped.
 *
 * **Bottom actions** (grid layout, 1/3 + 2/3 split):
 * - **Clear** — resets the entire capture flow
 * - **Analyze** — sends photos + hint to AI processing
 *
 * Entered from the `capturing` state via the photo tray pill tap.
 * Closing the drawer (drag-down) resets the capture flow.
 */
</script>

<script setup lang="ts">
import { ref } from "vue"

import { ICON } from "@/lib/icons"

defineProps<{
  /** Whether the drawer is open (bound to flow.isPhotoStrip). */
  open: boolean
  /** Array of photo data URLs to display as thumbnails. */
  photos: string[]
  /** Current hint text. Two-way bound via update:hint. */
  hint: string
  /** Whether voice recording is active for the hint field. */
  voiceActive: boolean
  /** Scanned barcode that will be attached as asset_code to the next item. */
  pendingAssetCode: string | null
}>()

defineEmits<{
  /** Fired when the drawer is opened or closed (drag-down to close). */
  "update:open": [value: boolean]
  /** Fired when the "+" add photo button is tapped. */
  addPhoto: []
  /** Fired when a photo's remove button (x) is tapped. */
  removePhoto: [index: number]
  /** Fired when the hint text changes (two-way binding). */
  "update:hint": [value: string]
  /** Fired when the "Analyze" button is tapped. Starts AI processing. */
  analyze: []
  /** Fired when the "Clear" button is tapped. Resets the capture flow. */
  clear: []
  /** Fired when the voice hint mic button is tapped. */
  toggleVoice: []
  /** Fired when the pending asset code dismiss button is tapped. */
  clearAssetCode: []
}>()

/** Controls visibility of the hint input field. Hidden until user taps "Add a hint...". */
const showHint = ref(false)
</script>

<template>
  <UDrawer :open="open" @update:open="$emit('update:open', $event)">
    <template #content>
      <div class="flex flex-col gap-4 p-4">
        <!-- Pending asset code badge -->
        <div
          v-if="pendingAssetCode"
          class="bg-primary/10 text-primary flex items-center justify-between rounded-lg px-3 py-2 text-xs font-medium"
        >
          <span class="flex items-center gap-1.5">
            <UIcon name="i-lucide-qr-code" class="size-3.5" />
            Code: {{ pendingAssetCode }}
          </span>
          <button
            aria-label="Remove code"
            class="text-primary/60 hover:text-primary"
            @click="$emit('clearAssetCode')"
          >
            <UIcon name="i-lucide-x" class="size-3.5" />
          </button>
        </div>

        <!-- Thumbnails — TODO: extract SbThumbnail component (rounded image box
             with optional dismiss X). Reuse in photo strip, suggestion card,
             and ItemImageGallery. -->
        <div class="flex items-center gap-2">
          <div
            v-for="(photo, i) in photos"
            :key="i"
            class="bg-accented relative h-16 w-16 shrink-0 overflow-hidden rounded-lg"
          >
            <img
              v-if="photo"
              :src="photo"
              class="h-full w-full object-cover"
              alt="Captured photo"
            />
            <button
              aria-label="Remove photo"
              class="bg-inverted/60 text-inverted absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full text-xs"
              @click="$emit('removePhoto', i)"
            >
              &times;
            </button>
          </div>
          <button
            class="border-default flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border-2 border-dashed"
            @click="$emit('addPhoto')"
          >
            <UIcon :name="ICON.imagePlus" class="text-muted text-lg" />
          </button>
        </div>

        <!-- Hint (progressive disclosure) -->
        <button
          v-if="!showHint && !hint"
          class="text-primary text-left text-xs font-medium"
          @click="showHint = true"
        >
          Add a hint...
        </button>
        <div v-if="showHint || hint" class="flex items-center gap-2">
          <UInput
            :model-value="hint"
            placeholder='e.g. "Makita drill, serial on bottom..."'
            class="flex-1"
            autofocus
            @update:model-value="$emit('update:hint', $event as string)"
          />
          <UButton
            :icon="voiceActive ? ICON.micOff : ICON.mic"
            :aria-label="voiceActive ? 'Stop voice hint' : 'Voice hint'"
            :color="voiceActive ? 'error' : 'neutral'"
            :variant="voiceActive ? 'solid' : 'ghost'"
            size="lg"
            square
            @click="$emit('toggleVoice')"
          />
        </div>

        <!-- Actions -->
        <div class="flex w-full flex-col gap-2">
          <UButton block size="xl" @click="$emit('analyze')">Analyze</UButton>
          <UButton variant="ghost" color="neutral" block size="xl" @click="$emit('clear')">
            Clear
          </UButton>
        </div>
      </div>
    </template>
  </UDrawer>
</template>
